import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getComponentWeightInAmount,
  getComponentWeightInAmountUnit,
} from '../../utils/componentWeightDisplay';

const DishRecipeIngredientsInformation = ({ dish }) => {
  const { t } = useTranslation();

  const ingredients = Array.isArray(dish?.ingredients) ? dish.ingredients : [];
  const originalContents = dish?.original_contents || '';

  const hasIngredientsTable =
    ingredients.length > 0 &&
    ingredients.some((ingredient) => ingredient && (ingredient.name || ingredient.id));
  const hasIngredientsString = originalContents && originalContents.trim().length > 0;

  if (!dish || (!hasIngredientsTable && !hasIngredientsString)) {
    return null;
  }

  const getIngredientName = (ingredient) => {
    if (!ingredient) return 'N/A';
    return ingredient.name || ingredient.title || ingredient.label || 'N/A';
  };

  const getOriginalAmountAndUnit = (ingredient) => {
    if (!ingredient) return 'N/A';

    const value = ingredient.inserted_value;
    const unit = ingredient.chosen_unit;

    if (value && unit) {
      // Keep backend unit key "styck", but present it as "portion" in PDF.
      if (unit === 'styck') {
        return `1 ${t('portion')}`;
      }
      const cleanValue = parseFloat(value).toString();
      return `${cleanValue}${unit}`;
    }

    return 'N/A';
  };

  const getConvertedAmountAndUnit = (ingredient) => {
    if (!ingredient) return 'N/A';

    const amount = getComponentWeightInAmount(ingredient);
    const unit = getComponentWeightInAmountUnit();

    if (amount != null) {
      const rounded = Math.round(amount * 10) / 10;
      return `${Math.round(rounded)}${unit}`;
    }

    return 'N/A';
  };

  // Generate ingredients string from ingredients array, sorted by percentage descending
  const generateIngredientsString = () => {
    if (!ingredients || ingredients.length === 0) {
      return originalContents || '';
    }

    const formatPercentage = (value) => {
      const num = parseFloat(value);
      if (!Number.isFinite(num)) return null;
      const rounded = parseFloat(num.toFixed(1));
      if (!Number.isFinite(rounded)) return null;
      return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
    };

    // Sort ingredients by percentage in descending order
    const sortedIngredients = [...ingredients]
      .filter((ingredient) => ingredient && (ingredient.name || ingredient.id))
      .sort((a, b) => {
        const getPercentage = (ing) => {
          const pct = ing.percentage ?? ing.weight_in_percentage ?? ing.inserted_percentage;
          const num = parseFloat(pct);
          return Number.isFinite(num) ? num : 0;
        };
        return getPercentage(b) - getPercentage(a);
      });

    const parts = sortedIngredients.map((ingredient) => {
      const pctStr = formatPercentage(
        ingredient.percentage ?? ingredient.weight_in_percentage ?? ingredient.inserted_percentage
      );
      const name = getIngredientName(ingredient);
      return pctStr ? `${name} (${pctStr})` : name;
    }).filter((part) => part && part.length > 0);

    if (parts.length > 0) {
      return `Ingredienser: ${parts.join(', ')}`;
    }

    return originalContents || '';
  };

  const ingredientsString = hasIngredientsTable ? generateIngredientsString() : originalContents;

  return (
    <div className="px-4">
      <div className="flex flex-col gap-6">
        {hasIngredientsTable && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:bg-white">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-50">
              <h3 className="text-md font-medium text-gray-800">{t('Ingredienser')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 print:bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Ingrediens')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Enhet')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 lowercase tracking-wider">
                      {t('g/ml')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 print:bg-white">
                  {ingredients
                    .filter((ingredient) => ingredient && (ingredient.name || ingredient.id))
                    .sort((a, b) => {
                      const getPercentage = (ing) => {
                        const pct = ing.percentage ?? ing.weight_in_percentage ?? ing.inserted_percentage;
                        const num = parseFloat(pct);
                        return Number.isFinite(num) ? num : 0;
                      };
                      return getPercentage(b) - getPercentage(a);
                    })
                    .map((ingredient, index) => (
                      <tr key={ingredient.id || ingredient.name || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {getIngredientName(ingredient)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-center">
                          {getOriginalAmountAndUnit(ingredient)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-center">
                          {getConvertedAmountAndUnit(ingredient)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {hasIngredientsString && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:bg-white">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-50">
              <h3 className="text-md font-medium text-gray-800">{t('Innehållförteckning')}</h3>
            </div>
            <div className="p-4">
              <p className="whitespace-pre-wrap leading-relaxed text-gray-800">{ingredientsString}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DishRecipeIngredientsInformation;

